import {
  hummingbirdHeldModifier,
  tapBindingsCollide,
} from '@electron/shared/keyboard-accelerator';
import { observer } from 'mobx-react-lite';

import { Icons } from '@/assets/icons/icons';
import { HotkeyLabel } from '@/components/blocks/Hotkey';
import { Button, XStack } from '@/components/ui';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { KeyboardShortcutInput } from '@/components/ui/KeyboardShortcutInput';
import { DICTATION_INPUT_DEVICE_CONFIG } from '@/features/dictation/dictation-transcription-input-device.config';
import { electronApis } from '@/features/electron-api';
import { FEATURES, hasFeature } from '@/features/feature-flags';
import { TranscriptionInputDeviceDropdown } from '@/features/transcription-input-device';
import { type KeyName } from '@/lib/key';
import { settingsStore } from '@/stores/settings.store';

import { SettingsSection } from '../settings-section';

import { DictationReadiness } from './settings-dictation-readiness';

const DICTATION_SHORTCUT_PRESETS = ['double-tap-command'] as const;

function dictationShortcutRejection(shortcut: string): string | null {
  if (!tapBindingsCollide(shortcut, settingsStore.hummingbirdShortcut)) {
    return null;
  }
  return "Hover's shortcut is that key. Pick a different one.";
}

const HOLD_SHORTCUT_OPTIONS: { value: '' | KeyName; label: string }[] = [
  { value: '', label: 'Off' },
  { value: 'fn', label: 'Fn' },
  { value: 'control', label: 'Control' },
  { value: 'shift', label: 'Shift' },
  { value: 'option', label: 'Option' },
  { value: 'command', label: 'Command' },
];

const HoldShortcutDropdown = observer(() => {
  const selected = HOLD_SHORTCUT_OPTIONS.find(
    (option) => option.value === settingsStore.dictationHoldShortcut
  );
  const heldByHover = hummingbirdHeldModifier(
    settingsStore.hummingbirdShortcut
  );

  return (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger asChild>
        <Button variant="ghost" size="sm">
          <XStack className="items-center gap-1.5">
            {selected?.value ? (
              <HotkeyLabel keyName={selected.value} size="xs" />
            ) : (
              <span>Off</span>
            )}
            <Icons.chevronDown className="size-3" />
          </XStack>
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="end" sideOffset={8} className="w-44">
        {HOLD_SHORTCUT_OPTIONS.map((option) => (
          <DropdownMenu.Item
            key={option.value || 'off'}
            disabled={option.value === heldByHover}
            onSelect={() =>
              settingsStore.setDictationHoldShortcut(option.value)
            }
          >
            <XStack className="items-center gap-2">
              {option.value && <HotkeyLabel keyName={option.value} size="xs" />}
              <span>{option.label}</span>
            </XStack>
          </DropdownMenu.Item>
        ))}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
});

export const DictationSettings = observer(() => {
  return (
    <SettingsSection.Root>
      <SettingsSection.Header>
        <SettingsSection.Title>Dictation</SettingsSection.Title>
        <SettingsSection.Description>
          Press a shortcut, speak, and what you said is typed into whatever
          you're already writing in. Your voice is transcribed on this device.
        </SettingsSection.Description>
      </SettingsSection.Header>
      <SettingsSection.Group>
        <SettingsSection.Item>
          <SettingsSection.ItemHeader>
            <SettingsSection.ItemLabel>
              Enable dictation
            </SettingsSection.ItemLabel>
            <SettingsSection.ItemDescription>
              Speak into any text box instead of typing.
            </SettingsSection.ItemDescription>
          </SettingsSection.ItemHeader>
          <SettingsSection.Switch
            checked={settingsStore.dictationEnabled}
            onCheckedChange={(v) => {
              settingsStore.setDictationEnabled(v);
              if (v) void electronApis.dictation.ensureMicrophoneAccess();
            }}
          />
        </SettingsSection.Item>
        {settingsStore.dictationEnabled && (
          <SettingsSection.Item>
            <SettingsSection.ItemHeader>
              <SettingsSection.ItemLabel>
                Global Shortcut
              </SettingsSection.ItemLabel>
              <SettingsSection.ItemDescription>
                Press it to start speaking, and again to insert what you said.
                Escape discards it. Best for anything long.
              </SettingsSection.ItemDescription>
            </SettingsSection.ItemHeader>
            <KeyboardShortcutInput
              value={settingsStore.dictationShortcut}
              onChange={(v) => settingsStore.setDictationShortcut(v)}
              presets={DICTATION_SHORTCUT_PRESETS}
              validate={dictationShortcutRejection}
            />
          </SettingsSection.Item>
        )}
        {settingsStore.dictationEnabled && (
          <SettingsSection.Item>
            <SettingsSection.ItemHeader>
              <SettingsSection.ItemLabel>
                Hold to talk
              </SettingsSection.ItemLabel>
              <SettingsSection.ItemDescription>
                Hold to say something short.
                {settingsStore.dictationHoldShortcut === 'fn' &&
                  ' Fn needs macOS Keyboard settings, "Press 🌐 key to", set to Do Nothing.'}
              </SettingsSection.ItemDescription>
            </SettingsSection.ItemHeader>
            <HoldShortcutDropdown />
          </SettingsSection.Item>
        )}
        {settingsStore.dictationEnabled && (
          <SettingsSection.Item>
            <SettingsSection.ItemHeader>
              <SettingsSection.ItemLabel>Microphone</SettingsSection.ItemLabel>
              <SettingsSection.ItemDescription>
                Pin a mic for dictation, or follow the system default. Meetings
                keep their own.
              </SettingsSection.ItemDescription>
            </SettingsSection.ItemHeader>
            <TranscriptionInputDeviceDropdown
              config={DICTATION_INPUT_DEVICE_CONFIG}
            />
          </SettingsSection.Item>
        )}
        {settingsStore.dictationEnabled &&
          hasFeature(FEATURES.DICTATION_AI_CLEANUP) && (
            <SettingsSection.Item>
              <SettingsSection.ItemHeader>
                <SettingsSection.ItemLabel>
                  AI cleanup
                </SettingsSection.ItemLabel>
                <SettingsSection.ItemDescription>
                  Fixes punctuation and removes false starts and repeated words.
                  The text you dictate is sent to Littlebird to do this. Your
                  audio stays on this device. Turn this off to keep dictation
                  fully on this device.
                </SettingsSection.ItemDescription>
              </SettingsSection.ItemHeader>
              <SettingsSection.Switch
                checked={settingsStore.dictationAICleanupEnabled}
                onCheckedChange={(v) =>
                  settingsStore.setDictationAICleanupEnabled(v)
                }
              />
            </SettingsSection.Item>
          )}
        {settingsStore.dictationEnabled && <DictationReadiness />}
      </SettingsSection.Group>
      {settingsStore.dictationEnabled && (
        <XStack className="items-start gap-3 rounded-lg border border-border-strong bg-surface py-3 pl-4 pr-3">
          <Icons.info className="size-4 text-foreground-muted shrink-0 mt-0.5" />
          <span className="flex-1 text-sm text-foreground-subtle">
            Dictation and meeting notes share the microphone, so dictation won't
            start while a meeting is being recorded.
          </span>
        </XStack>
      )}
    </SettingsSection.Root>
  );
});
